import { useState, useEffect } from 'react';
import { Container, Typography, Box, Button, CircularProgress, Card, CardContent, Paper, Grid, Divider } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useUserContext } from 'src/components/ProtectedRoute/userProvider';
import { createStripeAccount, getUser } from 'src/apiCalls';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import logger from 'src/logger';

export default function StripeSetupPage() {
    const { user } = useUserContext();
    const isMobile = false;
    const [stripeOnboardingComplete, setStripeOnboardingComplete] = useState<boolean | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchUserData = async () => {
            if (user?.accessToken && user?.uid) {
                try {
                    const userData = await getUser(user.accessToken, user.uid);
                    if (userData) {
                        setStripeOnboardingComplete(userData.stripeOnboardingComplete);
                    } else {
                        setStripeOnboardingComplete(false);
                    }
                } catch (error) {
                    setStripeOnboardingComplete(false);
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [user]);

    const handleCreateStripeAccount = async () => {
        try {
            const data = await createStripeAccount(user.accessToken, user.uid);
            if (data.url) {
                window.open(data.url, '_blank');
            } else {
                logger.error('Failed to create Stripe account.');
            }
        } catch (error) {
            logger.error('Error during Stripe account creation:', error);
        }
    };

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        logger.info(`Copied to clipboard: ${text}`);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: '#f5f5f5',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 1,
            }}
        >

            {/* Content Container */}
            <Container
                maxWidth="lg"
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2,
                    boxShadow: 3,
                    p: 4,
                    mx: 2,
                    mb: 4,
                }}
            >
                {stripeOnboardingComplete ? (
                    // Success state content
                    <Box sx={{ textAlign: 'center' }}>
                        <CheckCircleIcon
                            sx={{
                                fontSize: 64,
                                color: 'success.main',
                                mb: 2
                            }}
                        />
                        <Typography variant="h4" component="h1" gutterBottom>
                            Stripe Account Active
                        </Typography>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Your payment account is set up and ready to receive payments.
                        </Typography>

                        {/* Rest of your success state content... */}
                    </Box>
                ) : (
                    // Setup state content
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <Typography variant="h3" component="h1" gutterBottom>
                                Set Up Your Payment Account
                            </Typography>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                Want to charge for revealing your catch locations? Set up your payment account now!
                            </Typography>
                        </Grid>

                        {/* Main call to action */}
                        <Grid item xs={12}>
                            <Paper 
                                elevation={3} 
                                sx={{ 
                                    p: 3, 
                                    textAlign: 'center',
                                    backgroundColor: 'rgba(236, 236, 255, 0.9)',
                                    border: '1px solid #c4c4ff'
                                }}
                            >
                                <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
                                    Ready to Start Earning?
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 3 }}>
                                    Set up your payment account to start charging for location access. When someone wants to know where you caught that monster fish, they'll need to pay you first!
                                </Typography>
                                {user && (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        size="large"
                                        onClick={handleCreateStripeAccount}
                                        startIcon={<AccountBalanceWalletIcon />}
                                        sx={{
                                            py: 1.5,
                                            px: 4,
                                            fontSize: '1.1rem',
                                            boxShadow: 3,
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: 5,
                                            },
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        Set Up Payment Account
                                    </Button>
                                )}
                            </Paper>
                        </Grid>

                        {/* Information for Stripe signup */}
                        <Grid item xs={12}>
                            <Typography variant="h5" component="h2" gutterBottom>
                                Important Details for Signup
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                You'll need the following information during your Stripe account setup:
                            </Typography>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ mb: 2, height: '100%' }}>
                                        <CardContent>
                                            <Typography variant="h6">Website URL</Typography>
                                            <Typography variant="body1" sx={{ mb: 1 }}>
                                                <code>mycatch.co.uk/user/{user?.uid}</code>
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Note: You will need this URL during the Stripe sign-up process. Please make a note of it.
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<ContentCopyIcon />}
                                                onClick={() => handleCopy(`mycatch.co.uk/user/${user?.uid}`)}
                                            >
                                                Copy URL
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ mb: 2, height: '100%' }}>
                                        <CardContent>
                                            <Typography variant="h6">Email Address</Typography>
                                            <Typography variant="body1" sx={{ mb: 1 }}>
                                                <code>{user?.email}</code>
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Note: Use this email address when Stripe requests contact information.
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<ContentCopyIcon />}
                                                onClick={() => handleCopy(`${user?.email}`)}
                                            >
                                                Copy Email
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>

                        {/* Informational sections */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h5" component="h2" gutterBottom>
                                    Why Set Up Payments?
                                </Typography>
                                <Typography variant="body1" color="text.secondary" paragraph>
                                    We use Stripe to handle all payments securely. When you set up your account, you can start charging for access to your catch locations. The platform takes a 30% cut from all sales, and Stripe handles the rest.
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="h5" component="h2" gutterBottom>
                                    Payout Information
                                </Typography>
                                <Typography variant="body1" color="text.secondary" paragraph>
                                    Payouts are managed automatically by Stripe on a daily rolling basis. Your earnings will be transferred directly to your bank account after Stripe processes the payments.
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Paper elevation={1} sx={{ p: 3, bgcolor: 'rgba(255, 255, 240, 0.7)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <InfoIcon sx={{ mr: 1, color: 'info.main' }} />
                                    <Typography variant="h5" component="h2">
                                        Need Help?
                                    </Typography>
                                </Box>
                                <Typography variant="body1" paragraph>
                                    If you encounter any issues during the Stripe setup process, please visit our feedback page for assistance.
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    color="primary" 
                                    href="/feedback" 
                                    sx={{ mt: 1 }}
                                >
                                    Get Support
                                </Button>
                            </Paper>
                        </Grid>

                        {/* Secondary call to action at the bottom */}
                        <Grid item xs={12} sx={{ mt: 2, textAlign: 'center' }}>
                            {user && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleCreateStripeAccount}
                                    startIcon={<AccountBalanceWalletIcon />}
                                    sx={{ mt: 2 }}
                                >
                                    Set Up Payment Account
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                )}
            </Container>
        </Box>
    );
}